import { GOAL_STATUSES, ALLOCATION_TYPES } from './constants';

const COLOR_PALETTE = [
  '#7C9E6F',
  '#B58A4F',
  '#5E81AC',
  '#A36F84',
  '#6F8C9E',
  '#9E7C6F',
  '#8FA17C',
  '#7C6FA1',
];

export function colorFromGoalId(id) {
  if (!id) return COLOR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
}

export function toneForGoalProgress(pct) {
  if (pct >= 100) return 'success';
  if (pct >= 50) return 'sage';
  if (pct >= 25) return 'warning';
  return 'danger';
}

export function isChecklistGoal(goal) {
  return goal?.measurement_type === 'checklist';
}

export function isActiveGoal(goal) {
  return goal?.status === GOAL_STATUSES.ACTIVE || goal?.status === GOAL_STATUSES.PAUSED;
}

const MONTH_SHORT_PT = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

export function sortGoalsByDisplayOrder(goals) {
  return [...goals].sort((a, b) => {
    const orderA = a.display_order ?? 0;
    const orderB = b.display_order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    if (a.target_date && b.target_date) {
      return a.target_date.localeCompare(b.target_date);
    }
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * Prioritário: sugestão da API → fallback 1.º activo por display_order.
 */
export function resolvePriorityGoal(activeGoals, suggestedGoal) {
  if (!activeGoals?.length) return null;
  if (suggestedGoal?.id) {
    const match = activeGoals.find((g) => g.id === suggestedGoal.id);
    if (match) return match;
  }
  return sortGoalsByDisplayOrder(activeGoals)[0] ?? null;
}

/** Objectivos activos para o mosaico (exclui o prioritário). */
export function goalsForMosaic(activeGoals, priorityId) {
  if (!priorityId) return sortGoalsByDisplayOrder(activeGoals);
  return sortGoalsByDisplayOrder(activeGoals.filter((g) => g.id !== priorityId));
}

/** Tag curta Essencial / Prazer / Investimento. */
export function goalAllocationShortLabel(allocation, pt) {
  const labels = pt?.goals?.vision?.allocation;
  if (!labels) return allocation || '';
  if (allocation === ALLOCATION_TYPES.LIFESTYLE) return labels.lifestyle;
  if (allocation === ALLOCATION_TYPES.PERSONAL_INVESTMENT) return labels.investment;
  return labels.essential;
}

/** Classes Tailwind para tag de classificação no vision board. */
export function goalAllocationTagClasses(allocation) {
  if (allocation === ALLOCATION_TYPES.LIFESTYLE) {
    return 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-200';
  }
  if (allocation === ALLOCATION_TYPES.PERSONAL_INVESTMENT) {
    return 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800/50 dark:bg-sky-950/40 dark:text-sky-200';
  }
  return 'border-sage-200 bg-sage-50 text-sage-800 dark:border-sage-800/50 dark:bg-sage-950/40 dark:text-sage-200';
}

/** "abril 2027" a partir de target_date ISO (YYYY-MM-DD). */
export function formatGoalTargetMonth(dateStr) {
  if (!dateStr) return null;
  const [year, month] = dateStr.split('-').map(Number);
  if (!year || !month) return null;
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
}

/**
 * Meses marcados no teaser calendário (1–12).
 * @returns {{ dream: Set<number>, duty: Set<number>, monthLabels: string[] }}
 */
export function buildGoalYearMarkers({ goals = [], annualMonths = [], year }) {
  const dream = new Set();
  const duty = new Set();

  goals.forEach((goal) => {
    if (!goal.target_date) return;
    const [y, m] = goal.target_date.split('-').map(Number);
    if (y === year && m >= 1 && m <= 12) {
      dream.add(m);
    }
  });

  annualMonths.forEach((monthData) => {
    if (monthData.items?.length > 0 && monthData.month >= 1 && monthData.month <= 12) {
      duty.add(monthData.month);
    }
  });

  return { dream, duty, monthLabels: MONTH_SHORT_PT };
}

export function hasYearCalendarMarkers(markers) {
  return markers.dream.size > 0 || markers.duty.size > 0;
}
